<script setup lang="ts">
    import type { AgentConversationListItem } from '~/composables/useAiAssistantPage';

    const { t } = useTranslation();

    const props = defineProps<{
        agentConversations: AgentConversationListItem[];
        currentAgentConversationId: string | null;
        hasMoreAgentConversations: boolean;
        isLoadingAgentConversations: boolean;
    }>();

    const emit = defineEmits<{
        'conversation-selected': [id: string];
        'new-conversation': [];
        'load-more': [];
    }>();

    interface AgentConversationGroup {
        label: string;
        agentConversations: AgentConversationListItem[];
    }

    function groupAgentConversationsByDate(agentConversations: AgentConversationListItem[]): AgentConversationGroup[] {
        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const weekStart = new Date(todayStart);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + (weekStart.getDay() === 0 ? -6 : 1));
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

        const buckets: Record<string, AgentConversationListItem[]> = {
            today: [],
            week: [],
            month: [],
            older: [],
        };

        for (const agentConversation of agentConversations) {
            const date = new Date(agentConversation.updated_at);
            if (date >= todayStart) {
                buckets.today.push(agentConversation);
            } else if (date >= weekStart) {
                buckets.week.push(agentConversation);
            } else if (date >= monthStart) {
                buckets.month.push(agentConversation);
            } else {
                buckets.older.push(agentConversation);
            }
        }

        const groups: AgentConversationGroup[] = [];

        if (buckets.today.length) groups.push({ label: t('ai.time.today'), agentConversations: buckets.today });
        if (buckets.week.length) groups.push({ label: t('ai.time.this_week'), agentConversations: buckets.week });
        if (buckets.month.length) groups.push({ label: t('ai.time.this_month'), agentConversations: buckets.month });
        if (buckets.older.length) groups.push({ label: t('ai.time.older'), agentConversations: buckets.older });

        return groups;
    }

    const groupedAgentConversations = computed(() => groupAgentConversationsByDate(props.agentConversations));

    const sentinel = ref<HTMLElement | null>(null);
    const scrollContainer = ref<HTMLElement | null>(null);

    onMounted(() => {
        if (!sentinel.value || !scrollContainer.value) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && props.hasMoreAgentConversations && !props.isLoadingAgentConversations) {
                    emit('load-more');
                }
            },
            { root: scrollContainer.value, rootMargin: '100px' },
        );

        observer.observe(sentinel.value);

        onBeforeUnmount(() => {
            observer.disconnect();
        });
    });
</script>

<template>
    <div class="flex flex-col h-full overflow-hidden text-white">
        <!-- Header: spark glyph + title -->
        <div class="px-5 pt-[30px] flex items-center gap-[10px] flex-shrink-0">
            <i class="fa-jelly fa-regular fa-sparkles text-[18px] text-white flex-shrink-0" aria-hidden="true" />
            <span class="text-[16px] font-bold font-bricolage leading-none">{{ $t('ai.sidebar.title') }}</span>
        </div>

        <!-- New conversation button — DS yellow, no icon -->
        <div class="px-5 pt-[30px] flex-shrink-0">
            <button
                type="button"
                class="w-full h-[37px] px-[14px] inline-flex items-center justify-center rounded-md text-[14px] font-medium text-black bg-branding-brand-yellow border border-[#E8B102] shadow-sm hover:bg-[#FFD02A] transition-colors cursor-pointer"
                @click="emit('new-conversation')"
            >
                {{ $t('ai.sidebar.new_conversation') }}
            </button>
        </div>

        <!-- Conversation list -->
        <div ref="scrollContainer" class="flex-1 overflow-y-auto overflow-x-hidden nice-scrollbar px-5 pb-5 pt-[30px] min-w-0">
            <div
                v-for="(group, groupIndex) in groupedAgentConversations"
                :key="group.label"
                :class="groupIndex === groupedAgentConversations.length - 1 ? '' : 'mb-[30px]'"
            >
                <div class="font-bricolage font-bold text-[15px] leading-[15px] text-[#FFFFF5] mb-[15px]">
                    {{ group.label }}
                </div>
                <div class="flex flex-col gap-[10px]">
                    <AiPageConversationItem
                        v-for="agentConversation in group.agentConversations"
                        :key="agentConversation.id"
                        :agent-conversation="agentConversation"
                        :active="agentConversation.id === currentAgentConversationId"
                        @click="emit('conversation-selected', agentConversation.id)"
                    />
                </div>
            </div>

            <!-- Empty state -->
            <div
                v-if="!isLoadingAgentConversations && agentConversations.length === 0"
                class="px-3 py-6 text-center text-[12px] text-white/50"
            >
                {{ $t('ai.sidebar.no_conversations') }}
            </div>

            <!-- Infinite scroll sentinel -->
            <div ref="sentinel" class="h-1" />

            <!-- Load more button (fallback when list is short) -->
            <div v-if="hasMoreAgentConversations && !isLoadingAgentConversations" class="px-3 py-2">
                <button
                    class="w-full text-center text-[12px] text-white/50 hover:text-white/80 py-1 transition-colors cursor-pointer"
                    @click="emit('load-more')"
                >
                    Charger plus...
                </button>
            </div>

            <!-- Loading spinner -->
            <div v-if="isLoadingAgentConversations" class="flex justify-center py-3">
                <i class="fa-regular fa-spinner-third fa-spin text-white/60 text-[14px]" />
            </div>
        </div>
    </div>
</template>
